class Stage {
    constructor(element, options) {
        // Default options
        const defaults = {
            initAttr: 'data-research-ai-landingpage-stage',
            demoAttr: 'data-research-ai-landingpage-stage-demo',
            loggedInAttr: 'data-research-ai-landingpage-stage-logged-in',
            promptValue: 'prompt',
            formValue: 'form',
            choiceValue: 'choice',
            submitValue: 'submit',
            bulbsValue: 'bulbs',
            typeTimer: null,
        };

        this.isTyping = false;

        // Settings
        this.settings = Object.assign({}, defaults, options);
        this.selectedCategory = 'none';

        // Elements
        this.$stage = element;
        this.$form = this.$stage.querySelector(`[${ this.settings.initAttr }="${ this.settings.formValue }"]`);
        this.$prompt = this.$stage.querySelector(`[${ this.settings.initAttr }="${ this.settings.promptValue }"]`);
        this.$bulbs = this.$stage.querySelector(`[${ this.settings.initAttr }="${ this.settings.bulbsValue }"]`);
        this.$textarea = this.$stage.querySelector('textarea');
        this.$submitCta = this.$stage.querySelector(`[${ this.settings.initAttr }="${ this.settings.submitValue }"]`);
        this.$$choices = this.$stage.querySelectorAll(`[${ this.settings.initAttr }="${ this.settings.choiceValue }"]`);
        this.$$videos = this.$stage.querySelectorAll('video');

        // Options
        this.loggedIn = this.$stage.hasAttribute(this.settings.loggedInAttr);
        this.characterLength = 100;
        this.indicatorActive = false;
        this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

        /**
         * @description Selection status of the choices
         * @type {Array}
         * @property {number} choice - Index of the selected choice
         * @property {boolean} selected - If the choice has been selected
         * @property {number} currentCharIndex - The current character of the choice
         */
        this.selectionStatus = [];
    }

    initialize() {
        if (this.loggedIn && this.$bulbs) {
            window.setTimeout(() => {
                this.$bulbs.classList.add('researchAiLandingpage-stage__bulbs--small');
            }, 1250);
        }

        if (this.$form && this.$prompt && this.$submitCta && this.$$choices.length > 0) {
            this.setEvents();
        }

        if (this.$$videos.length > 0 && !this.isMobile()) {
            this.$$videos.forEach(video => {
                this.loadVideo(video);
            });
        }
    }

    isMobile(){
        return this.isTouchDevice && window.innerWidth <= 768;
    }

    loadVideo(video) {
        const sources = video.querySelectorAll('source');

        sources.forEach(source => {
            source.src = source.getAttribute('data-src');
        });

        video.load();
        video.play();
    }

    setIndicatorWidth() {
        let progress = this.$textarea.value.length / this.characterLength;
        progress = progress >= 1 ? 1 : progress?.toFixed(2);

        this.$prompt.style.setProperty('--progress', progress);
    }

    typeInDemoText(text, category, callback) {
        // Get the current selection category
        const currentSelection = this.selectionStatus.find(selection => selection.choice === this.selectedCategoryIndex);

        if (this.isMobile()) {
            this.$textarea.value = text;
            if (typeof callback === 'function') {
                callback();
            }

            return;
        }

        if (this.settings.typeTimer !== null) {
            window.clearTimeout(this.settings.typeTimer);
        }

        this.$textarea.value = '';
        this.$textarea.style.pointerEvents = 'none';

        // Get the current character index
        let currentCharIndex = currentSelection.currentCharIndex || 0;

        const typeNextChar = () => {
            // Starting the typing from the beginning or continue from the last character typed for each category item
            if (this.$textarea.value.length < currentCharIndex) {
                this.$textarea.value = text.substring(0, currentCharIndex);
            }

            if (currentCharIndex < text.length) {
                this.$textarea.value += text.charAt(currentCharIndex);
                currentCharIndex++;
                // Update the current character index
                currentSelection.currentCharIndex = currentCharIndex;

                if (this.selectedCategory !== category) {
                    return;
                }

                this.settings.typeTimer = window.setTimeout(typeNextChar, 30);
                this.setIndicatorWidth();
            } else {
                this.$textarea.style.pointerEvents = '';
                window.clearTimeout(this.settings.typeTimer);
                this.settings.typeTimer = null;

                if (typeof callback === 'function') {
                    callback();
                }
            }
        };

        typeNextChar();
    }

    toggleSubmitState() {
        if (this.$textarea.value.length > 0) {
            this.$submitCta.disabled = false;
        } else {
            this.$submitCta.disabled = true;
        }
    }

    submitPrompt() {
        const currentAction = this.$form.getAttribute('action');
        const actionPartials = currentAction.split('?');
        const baseAction = actionPartials[0];

        if (this.loggedIn) {
            let promptInput = this.$textarea.value || this.demoText;
            if (this.isTyping) {
                promptInput = this.demoText;
            }
            const encodedPromptInput = encodeURIComponent(promptInput);
            this.$form.action = `${ baseAction }?q=${ encodedPromptInput }`;
        } else {
            this.$form.action = `${ baseAction }?token=demo${ this.selectedCategoryIndex + 1 }`;
        }

        this.$form.submit();
    }

    setEvents() {
        // Click on category choices
        this.$$choices.forEach(($choice, index) => {
            $choice.addEventListener('click', (e) => {
                this.$submitCta.disabled = false;
                if (!this.indicatorActive) {
                    this.indicatorActive = true;
                    this.$prompt.classList.add('researchAiLandingpage-stage__promptTextarea--indicator');
                }

                if (e.target.type === 'radio') {
                    // Check if the choice has already been selected
                    const hasSelected = this.selectionStatus.some(selection => selection.choice === index);

                    // Update the selection status
                    this.selectionStatus = this.selectionStatus.map(selection => ({
                        ...selection,
                        selected: selection.choice === index,
                    }));

                    // If the choice has not been selected, add it to the selection status
                    if (!hasSelected) {
                        this.selectionStatus = [
                            ...this.selectionStatus,
                            {
                                choice: index,
                                selected: true,
                                currentCharIndex: 0,
                            },
                        ];
                    }

                    this.$prompt.style.setProperty('--progress', 0);
                    this.selectedCategory = e.target.value;
                    this.selectedCategoryIndex = index;
                    this.demoText = e.target.getAttribute(this.settings.demoAttr);
                    this.characterLength = this.demoText.length;

                    if (!this.loggedIn) {
                        // Enable the textarea and submit button for not logged in users
                        this.$submitCta.style.pointerEvents = 'all';
                        this.$prompt.classList.add('researchAiLandingpage-stage__promptTextarea--visible');

                        if (this.$bulbs) {
                            this.$bulbs.classList.add('researchAiLandingpage-stage__bulbs--small');
                        }
                    }

                    if (this.demoText !== null) {
                        window.setTimeout(() => {
                            this.isTyping = true;
                            this.typeInDemoText(this.demoText, this.selectedCategory, () => {
                                this.$textarea.style.pointerEvents = 'all';
                                this.$submitCta.style.pointerEvents = 'all';
                                this.isTyping = false;
                            });
                        }, 300);
                    }
                }
            });
        });

        // Input in textarea
        this.$textarea.addEventListener('input', (event) => {
            if (!this.indicatorActive) {
                this.indicatorActive = true;
                this.$prompt.classList.add('researchAiLandingpage-stage__promptTextarea--indicator');
            }
            if (event.target.value === '') {
                this.$submitCta.disabled = true;
            } else {
                this.$submitCta.disabled = false;
            }
            this.setIndicatorWidth();
        });

        // Update action and submit
        this.$form.addEventListener('submit', (e) => {
            e.preventDefault();
            this.submitPrompt();
        });
    }
}

export { Stage };
